import { useState, useEffect, useMemo } from "react";
import "./ExchangeForVariant.css";
import "./ExchangeForProduct.css";
import { Item } from "../shared/ProductCard";
import { BsX } from 'react-icons/bs';
import NavButtons from "./NavButtons";
import React from "react";
import { ShopifyProduct } from "./ProductResponse";
import { ShopifyCard } from "../shared/ProductVariantCards";
import { ScreenSize } from "../shared/ScreenSize";
import { SearchBar } from "../shared/SearchBar";
import { useTheme } from "../../context/PortalThemeContext";
import { fetchWithSessionToken } from "../shared/fetchWithSessionToken";
import { CHAT_API_BASE } from "../../config/api";
import { Spinner } from "react-bootstrap";
import { ProductCarousel } from "../shared/ProductCarousel";

interface ExchangeForProductProps {
    onNext: () => void;
    handleCancel: () => void;
    shopifyProducts: ShopifyProduct[];
    onSelectProduct: (product: ShopifyProduct) => void;
    selectedItemToReturn: Item;
}

export enum ExchangeType {
    REFUND = 'refund',
    EXCHANGE = 'exchange',
}

interface SelectedProduct {
    category: string,
    product: ShopifyProduct,
}

const ExchangeForProduct = ({
    onNext,
    handleCancel,
    shopifyProducts,
    onSelectProduct,
    selectedItemToReturn,
}: ExchangeForProductProps) => {

    const [productTagMap, setProductTagMap] = useState<Record<string, ShopifyProduct[]>>({});
    const [isLoading, setIsLoading] = useState(true);


    useEffect(() => {
        const shopId = theme.store;

        const fetchPopular = async () => {
            setIsLoading(true);
            // Assume you already have full `shopifyProducts` prop
            const idToProduct = Object.fromEntries(shopifyProducts.map(p => [p.id, p]));

            const tagMap: Record<string, ShopifyProduct[]> = {};

            try {
                const res = await fetchWithSessionToken(
                    `${CHAT_API_BASE}/companies/orders/popular?shopId=${shopId}`);

                if (!res.ok) {
                    setIsLoading(false);
                    return;
                }

                const popular = await res.json();

                if (popular) {
                    popular.forEach((item: any) => {
                        if (idToProduct[item.productId]) {
                            if (!tagMap["Best Sellers"]) tagMap["Best Sellers"] = [];
                            tagMap["Best Sellers"].push(idToProduct[item.productId]);
                        }
                    });
                }

                const productId = selectedItemToReturn?.product_id;

                if (typeof productId === 'number') {
                    const res2 = await fetchWithSessionToken(
                        `${CHAT_API_BASE}/companies/orders/frequently-bought-with?shopId=${shopId}&productId=${productId}`);
                    const related = await res2.json();

                    if (related) {
                        related.forEach((item: any) => {
                            if (idToProduct[item.productId]) {
                                if (!tagMap["Frequently Bought With"]) tagMap["Frequently Bought With"] = [];
                                tagMap["Frequently Bought With"].push(idToProduct[item.productId]);
                            }
                        });
                    }

                }

                setProductTagMap(tagMap);
                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching products:", error);
                setIsLoading(false);
            }
        };

        fetchPopular();
    }, []);

    const responsive = {

        largest: {
            breakpoint: { max: 5000, min: ScreenSize.XXL },
            items: 3,
            partialVisibilityGutter: 15
        },
        large: {
            breakpoint: { max: ScreenSize.XXL, min: ScreenSize.LG },
            items: 2,
            partialVisibilityGutter: 15
        },
        medium: {
            breakpoint: { max: ScreenSize.LG, min: ScreenSize.SM },
            items: 1,
            partialVisibilityGutter: 15
        },
        small: {
            breakpoint: { max: ScreenSize.SM, min: 0 },
            items: 1,
            partialVisibilityGutter: 15
        },
    };

    const theme = useTheme();
    const [selectedProduct, setSelectedProduct] = useState<SelectedProduct | null>(null);
    const [selectionError, setSelectionError] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const filteredProductTagMap = useMemo(() => {
        if (!searchQuery) return productTagMap;

        const lowerQuery = searchQuery.toLowerCase();

        return Object.entries(productTagMap).reduce((acc, [tag, products]) => {
            const filtered = products.filter((product: ShopifyProduct) =>
                product.title.toLowerCase().includes(lowerQuery)
            );
            if (filtered.length > 0) acc[tag] = filtered;
            return acc;
        }, {} as Record<string, ShopifyProduct[]>);
    }, [searchQuery, productTagMap]);

    const handleNext = () => {
        if (!selectedProduct) {
            setSelectionError(true);
            return;
        }

        onSelectProduct(selectedProduct.product);
        onNext();
    };

    return (
        <div className="card" style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
            <div className="product-info-container">

                <div
                    className="d-flex justify-content-between align-items-start sticky-top w-100"
                    style={{ background: 'white', zIndex: 1001 }}
                >
                    <div style={{ flex: 1 }}>
                        <div style={{ padding: '1rem' }} />
                        <h1 className="variant-option-title">Pick out your exchange</h1>
                        <SearchBar query={searchQuery} setQuery={setSearchQuery} placeholder="Search the store" />
                    </div>

                    <div className="close-button" onClick={handleCancel}>
                        <div style={{ padding: '1rem' }} />
                        <BsX size={40} />
                    </div>
                </div>


                <div className="product-card-grid">
                    {isLoading && (
                        <Spinner style={{ justifySelf: 'center', alignSelf: 'center' }} />
                    )}
                    {!isLoading && (
                        Object.keys(filteredProductTagMap).length > 0 ? (
                            Object.entries(filteredProductTagMap).map(([tag, products]) => (
                                <React.Fragment key={tag}>
                                    <div className="card-row-header">{tag}</div>
                                    <ProductCarousel items={
                                        products.map((product) => {
                                            return (
                                                <div key={product.id + tag} className="embla__slide" onClick={() => {
                                                    setSelectedProduct({ category: tag, product });
                                                    setSelectionError(false);
                                                }} >
                                                    <ShopifyCard
                                                        className={`selectable-card ${selectedProduct?.product.id === product.id && selectedProduct?.category === tag ? 'selected' : ''}`}
                                                        cardStyle={false}
                                                        shopifyProduct={product}
                                                    />
                                                </div>
                                            );
                                        })
                                    } />
                                </React.Fragment>
                            ))
                        ) : <p style={{ justifySelf: 'center', alignSelf: 'center', textAlign: 'center' }}>No products found.</p>)}
                </div>
            </div>

            <div className="d-flex flex-column align-items-end sticky-bottom" style={{ background: 'white' }}
            >
                {selectionError && <p className="no-selection-error">Please select an option to proceed.</p>}
                <NavButtons
                    style={{ background: 'white' }}
                    backButtonText="Cancel"
                    nextButtonText="Next"
                    handleBack={handleCancel}
                    handleNext={handleNext}
                    disabled={!selectedProduct}
                    onDisabledClick={handleNext}
                />
            </div>

        </div>
    );
};

export default ExchangeForProduct;
